// Import from NPM
// -------------------------------------
import React from "react";
import localforage from "localforage";
import { Router, Route, hashHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import loadable from "@loadable/component";
import "semantic-ui-css/semantic.min.css";
import "config/initializers/overrule.css";
import { Helmet } from "react-helmet";
import { Crisp } from "crisp-sdk-web";

// Import from Config
// -------------------------------------
import {
    Store,
    persistStore,
    persistStoreConfig,
} from "backend/storage/app.store";
import { getAppConfig } from "config/client.config";
import { appConfig } from "config/initializers/config.app";
import { LOGOUT } from "app/authentication/redux/Authentication.action.react";

// Import Helpers
// -------------------------------------
import { Loading } from "app/appComponents/Loading.react";

const LoadableAuthenticationContainer = loadable(
    () =>
        import(
            "app/authentication/AuthenticationContainer.react" /* webpackChunkName: "authentication" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAdminHomeContainer = loadable(
    () =>
        import(
            "app/adminPanels/AdminHomeContainer.react" /* webpackChunkName: "admin" */
        ),
    {
        fallback: <Loading />,
    }
);

const LoadableWelcome = loadable(
    () =>
        import(
            "app/home/WelcomeContainer.react" /* webpackChunkName: "home" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableLibraryContainer = loadable(
    () =>
        import(
            "app/blog/LibraryContainer.react" /* webpackChunkName: "library" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAnalyticsDashboardContainer = loadable(
    () =>
        import(
            "app/blog/AnalyticsDashboardContainer.react" /* webpackChunkName: "analytics" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableLeadsContainer = loadable(
    () =>
        import("app/blog/LeadsContainer.react" /* webpackChunkName: "leads" */),
    {
        fallback: <Loading />,
    }
);
const LoadableAccountContainer = loadable(
    () =>
        import(
            "app/blog/AccountContainer.react" /* webpackChunkName: "account" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableBloggingContainer = loadable(
    () =>
        import(
            "app/blog/BloggingContainer.react" /* webpackChunkName: "previewer" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAmplayfierBaseContainer = loadable(
    () =>
        import(
            "app/blog/AmplayfierBaseContainer.react" /* webpackChunkName: "ampbase" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAmplayfierContentContainer = loadable(
    () =>
        import(
            "app/blog/AmplayfierContentContainer.react" /* webpackChunkName: "ampedit" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAmplayfierPropertiesContainer = loadable(
    () =>
        import(
            "app/blog/AmplayfierPropertiesContainer.react" /* webpackChunkName: "ampprops" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAmplayfierShareContainer = loadable(
    () =>
        import(
            "app/blog/AmplayfierShareContainer.react" /* webpackChunkName: "ampshare" */
        ),
    {
        fallback: <Loading />,
    }
);
const LoadableAmplayfierAnalyticsContainer = loadable(
    () =>
        import(
            "app/blog/AmplayfierAnalyticsContainer.react" /* webpackChunkName: "ampanalytics" */
        ),
    {
        fallback: <Loading />,
    }
);

class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rehydrated: false,
        };
        this.requireLogin = this.requireLogin.bind(this);
        this.alreadyLoggedIn = this.alreadyLoggedIn.bind(this);
        this.manageStore = window.manageStore = this.manageStore.bind(this);
        this.appType = window.appType = "drive";
        this.manageStore();
    }

    componentDidMount() {
        if (process.env.NODE_ENV !== "developement")
            Crisp.configure("ffb2439b-a5da-4e2f-9010-38472ca41dea");
    }

    manageStore = (purge) => {
        if (purge === "purge") {
            persistStore(Store, persistStoreConfig).purge();
            localforage.removeItem("tokenHash");
        } else {
            if (window.localStorage) {
                persistStore(Store, persistStoreConfig, () => {
                    let existing_user = window.localStorage.getItem(
                        "reduxPersist:auth"
                    );
                    if (existing_user) {
                        localforage.setItem("reduxPersist:auth", existing_user);
                    }
                    if (
                        Store.getState().auth.isUserLoggedIn &&
                        Store.getState().auth.user &&
                        Store.getState().auth.user.username.startsWith("ip")
                    ) {
                        localforage.removeItem("tokenHash", null).then(() => {
                            return Promise.resolve(
                                Store.dispatch({
                                    type: LOGOUT,
                                    status: "success",
                                })
                            ).then(() => {
                                this.setState({ rehydrated: true });
                            });
                        });
                    } else {
                        this.setState({ rehydrated: true });
                    }
                });
            }
        }
    };

    requireLogin(nextState, replace, callback) {
        if (!Store.getState().auth.isUserLoggedIn) {
        } else {
            this.setupCrispConfig();
        }
        callback();
    }
    requireAdminLogin(nextState, replace, callback) {
        if (
            !Store.getState().auth.isUserLoggedIn ||
            Store.getState().auth.user.license.license_type !== "Superadmin"
        ) {
            replace("/login");
        }
        callback();
    }

    alreadyLoggedIn(nextState, replace, callback) {
        if (Store.getState().auth.isUserLoggedIn) {
            this.setupCrispConfig();
            replace(appConfig.auth.routes.afterSignIn);
        }
        callback();
    }

    setupCrispConfig() {
        if (process.env.NODE_ENV !== "development") {
            // let setConfig = true
            // if (Crisp && Crisp.user && Crisp.user !== undefined) {
            //     if (Crisp.user.getEmail() === Store.getState().auth.user.email) {
            //         setConfig = false;
            //     }
            // }
            // if (setConfig) {
                Crisp.setTokenId(Store.getState().auth.user.id);
                Crisp.user.setEmail(
                    Store.getState().auth.user.email,
                );
                Crisp.user.setNickname(
                    `${Store.getState().auth.user.first_name} ${Store.getState().auth.user.last_name}`
                );
                window.localStorage.setItem("crispsetup", true);
                Crisp.load();
            // }
        }

    }

    render() {
        let noOverflow = {
            height: "100%",
            // height: "100vh",
        };
        let appStyle = {
            ...getAppConfig().appStyle,
            config: "calc(100% - 60px)", // To adjust for the navbar or the title bar
            position: "relative",
            height: "100%",
            background: "inherit",
        };

        let returnValue = (
            <div style={noOverflow}>
                <Helmet>
                    <meta charset="utf-8" />
                    <meta
                        name="viewport"
                        content="user-scalable=0,
                   initial-scale=1.0,
                   maximum-scale=3.0,
                   minimum-scale=1.0,
                   width=device-width"
                    />
                    <meta
                        http-equiv="Content-Security-Policy"
                        content="default-src * 'unsafe-inline' 'self' data: gap: https://ssl.gstatic.com 'unsafe-eval';img-src * 'self' data: blob: https: android-webview-video-poster:; style-src 'self' 'unsafe-inline' * https:; media-src *  'self' data: blob: https:;"
                    />
                    <meta name="author" content="QuoDeck Technologies PLC" />
                    <meta
                        name="description"
                        content="Are you ready to experience Amplayfy?"
                    />
                    <meta name="theme-color" content="#000000" />

                    <link rel="manifest" href="manifest.json" />
                    <link rel="shortcut icon" href="assets/icons/favicon.ico" />
                    <title>
                        {appConfig.appName
                            ? appConfig.appName.dashboard
                            : "Amplayfy: Create interactive marketing content"}
                    </title>
                </Helmet>
                <div style={appStyle}>
                    <Router history={syncHistoryWithStore(hashHistory, Store)}>
                        <Route
                            path="/:appName/login"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.alreadyLoggedIn}
                        />
                        <Route
                            path="/login"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.alreadyLoggedIn}
                        />
                        <Route
                            path="/register"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.alreadyLoggedIn}
                        />
                        <Route
                            path="/forgotPassword"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.alreadyLoggedIn}
                        />
                        <Route
                            path="/dashboard"
                            component={LoadableAnalyticsDashboardContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/library"
                            component={LoadableLibraryContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/leads"
                            component={LoadableLeadsContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/account"
                            component={LoadableAccountContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/blogging"
                            component={LoadableBloggingContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/articles/:id/show"
                            component={LoadableAmplayfierBaseContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/articles/:id/edit"
                            component={LoadableAmplayfierBaseContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/articles/new"
                            component={LoadableAmplayfierBaseContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/contentEditor/:id"
                            component={LoadableAmplayfierContentContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/propertiesEditor/:id"
                            component={LoadableAmplayfierPropertiesContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/shareEditor/:id"
                            component={LoadableAmplayfierShareContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/analyticsViewer/:id"
                            component={LoadableAmplayfierAnalyticsContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/"
                            component={LoadableWelcome}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/logout"
                            component={LoadableAuthenticationContainer}
                            onEnter={this.requireLogin}
                        />
                        <Route
                            path="/admin"
                            component={LoadableAdminHomeContainer}
                            onEnter={this.requireAdminLogin}
                        />
                    </Router>
                </div>
            </div>
        );
        if (!this.state.rehydrated) {
            returnValue = <Loading />;
        }
        return returnValue;
    }
}

export default App;
